import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { subscribe } from 'lib/pusher';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SnapshotStatusItem {
  label: string;
  value: string;
  details?: string;
  name: string; // Pusher event name for matching
}

interface SnapshotStepEvent {
  name: string;
  label: string;
  step_type: string;
  order: number;
}

interface SnapshotProgressState {
  statusItems: SnapshotStatusItem[];
  overallStatus: string;
}

type SnapshotProgressAction =
  | { type: 'reset' }
  | { type: 'initialize'; steps: SnapshotStepEvent[] }
  | {
      type: 'updateStep';
      name: string;
      status: string;
      details?: string | null;
    }
  | { type: 'setOverallStatus'; overallStatus: string };

interface WorkflowStepData {
  status: string;
  details?: string | null;
  step_type?: string;
  href?: string;
  output?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Status display mapping (same as CRM — will be shared later)
// ---------------------------------------------------------------------------

const STATUS_DISPLAY_MAP: Record<string, string> = {
  pending: 'Pending',
  processing: 'In Progress',
  processed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
  unchanged: 'Unchanged',
  unnecessary: 'Unnecessary',
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const snapshotProgressReducer = (
  state: SnapshotProgressState,
  action: SnapshotProgressAction
): SnapshotProgressState => {
  switch (action.type) {
    case 'reset':
      return { statusItems: [], overallStatus: 'pending' };
    case 'initialize':
      return {
        ...state,
        statusItems: action.steps.map((step) => ({
          label: `${step.label}:`,
          value: 'Pending',
          name: step.name,
        })),
      };
    case 'updateStep':
      return {
        ...state,
        statusItems: state.statusItems.map((item) => {
          if (item.name !== action.name) return item;
          // Don't regress from Completed
          if (item.value === 'Completed' && action.status !== 'processed')
            return item;
          return {
            ...item,
            value: STATUS_DISPLAY_MAP[action.status] || action.status,
            details: action.details ?? undefined,
          };
        }),
      };
    case 'setOverallStatus':
      return { ...state, overallStatus: action.overallStatus };
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseDpsSnapshotProgressParams {
  channel: string | null;
  steps: SnapshotStepEvent[] | null;
}

interface UseDpsSnapshotProgressReturn {
  statusItems: SnapshotStatusItem[];
  overallStatus: string;
  isBuilding: boolean;
  isComplete: boolean;
  isFailed: boolean;
}

/**
 * Subscribes to a DPS SnapshotProcess Pusher channel and tracks per-step
 * progress. Feeds the WorkflowStatusPopover component.
 *
 * @param {string | null} channel - Pusher channel from the schedule response (dps_snapshot_{id})
 * @param {DpsSnapshotStepEvent[] | null} steps - Step event definitions from the schedule response
 * @return {UseDpsSnapshotProgressReturn} Progress state for the WorkflowStatusPopover.
 */
export const useDpsSnapshotProgress = ({
  channel,
  steps,
}: UseDpsSnapshotProgressParams): UseDpsSnapshotProgressReturn => {
  const [state, dispatch] = useReducer(snapshotProgressReducer, {
    statusItems: [],
    overallStatus: 'pending',
  });

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  // Initialize status items when steps arrive, reset when cleared
  useEffect(() => {
    if (steps && steps.length > 0) {
      dispatch({ type: 'initialize', steps });
    } else {
      dispatch({ type: 'reset' });
    }
  }, [steps]);

  // Create step event handler
  const createStepHandler = useCallback(
    (eventName: string) => (data: WorkflowStepData) => {
      if (data?.status) {
        dispatch({
          type: 'updateStep',
          name: eventName,
          status: data.status,
          details: data.details,
        });
      }
    },
    []
  );

  // Pusher subscription
  useEffect(() => {
    if (!channel || state.overallStatus === 'processed') return;

    const subscription = subscribe(channel);

    // Bind overall process status
    subscription.bind(
      'crm-workflow-process-status-changed',
      (data: { overall_status?: string }) => {
        if (data?.overall_status) {
          dispatch({
            type: 'setOverallStatus',
            overallStatus: data.overall_status,
          });
        }
      }
    );

    // Bind per-step events
    const currentSteps = stepsRef.current || [];
    currentSteps.forEach((step) => {
      subscription.bind(step.name, createStepHandler(step.name));
    });

    // Do NOT call subscription.unsubscribe() here — it would kill the
    // entire Pusher channel, including bindings from useDpsScheduling
    // (which listens for slots_ready on the same channel). The scheduling
    // hook manages the channel lifecycle.
    return () => {
      subscription.unbind('crm-workflow-process-status-changed');
      currentSteps.forEach((step) => {
        subscription.unbind(step.name);
      });
    };
  }, [channel, state.overallStatus, createStepHandler]);

  return useMemo(
    () => ({
      statusItems: state.statusItems,
      overallStatus: state.overallStatus,
      isBuilding:
        state.overallStatus === 'processing' ||
        state.overallStatus === 'pending',
      isComplete: state.overallStatus === 'processed',
      isFailed: state.overallStatus === 'failed',
    }),
    [state]
  );
};
